import { faLocationDot } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { notification } from "antd";
import dayjs from "dayjs";
import React, { useMemo } from "react";
import { trackPromise } from "react-promise-tracker";
import { globalAppConfig } from "../../config";
import GlobalStoreContext from "../../context/GlobalStoreContext";
import { CalendarEvent } from "../../models/CalendarEvent";
import { Delivery } from "../../models/Delivery";
import PlotAPI from "../../plotAPI";
import ConfirmETA from "./confirmETAModal";

interface ShareETAProps {
    delivery: Delivery;
    calendarEvent: CalendarEvent;
    saveDelivery: (delivery: Delivery) => Promise<any>;
}
const ShareETA: React.FC<ShareETAProps> = (props) => {
    const context = React.useContext(GlobalStoreContext);

    const [disabled, setDisabled] = React.useState<boolean>(false);
    const [showConfirmETAModal, setShowConfirmETAModal] = React.useState<boolean>(false);
    const [deliveryCopy, setDeliveryCopy] = React.useState<Delivery>(props.delivery);

    React.useEffect(() => {
        setDeliveryCopy(props.delivery);
    }, [props.delivery]);

    const dropoff = useMemo(
        () =>
            context
                .mapAreasForProject(context.currentProjectInfo.unique_token)
                .find((item) => item.id === props.calendarEvent.deliveryDropOffId()),
        [props.delivery]
    );

    const getUserCoordinates = () => {
        return new Promise((res, rej) => {
            navigator.geolocation.getCurrentPosition(res, rej);
        });
    };
    const setRoute = (route: any) => {
        const distance = route.distance / 1609.344;
        const duration = route.duration / 60;

        const eta = dayjs().add(duration, "minute");

        const updatedDelivery = Object.create(deliveryCopy) as Delivery;

        updatedDelivery.deliveryRoute = route;
        updatedDelivery.eta = eta;
        updatedDelivery.etaSelected = duration;

        // They have manually selected a value instead of using the calculated one, so we would not have a travel distance, only time
        updatedDelivery.travelDistance = [15, 30, 60].includes(duration) ? null : distance;
        setDeliveryCopy(updatedDelivery);
        setShowConfirmETAModal(true);
        setDisabled(false);
    };
    const calculateETA = () => {
        setDisabled(true);
        const center = dropoff?.getMapObject(context)?.getCenter();
        const mapObjectCoordinates = { lon: center[0], lat: center[1] };

        if (!mapObjectCoordinates) {
            setDisabled(false);
            return;
        }

        trackPromise(
            getUserCoordinates()
                .then((res: any) => {
                    const userCoords = { lon: res.coords.longitude, lat: res.coords.latitude };
                    PlotAPI.getDirections(
                        context.currentProjectInfo.unique_token,
                        userCoords,
                        mapObjectCoordinates
                    ).then((res) => {
                        // TODO: Add good types here
                        // @ts-expect-error: This is not correctly typed but it exists
                        setRoute(res.routes[0]);
                    });
                })
                .catch(() => {
                    setDisabled(false);
                    notification.error({
                        duration: globalAppConfig.NOTIFICATION_DURATION,
                        message: "Geolocation Not Available",
                        description:
                            "Your Geolocation was not shared. Please grant permission to use your geolocation to automatically calculate your ETA.",
                    });
                })
        );
    };
    return (
        <section className="mx-4 mt-4">
            {props.delivery.eta && (
                <div className="mb-4 text-center">
                    <p className="text-sm text-white font-medium mb-1">Current Estimated Arrival Time</p>
                    <p className="text-2xl font-bold text-white">{dayjs(props.delivery.eta).format("h:mm A")}</p>
                    <p className="text-sm italic text-white">
                        Update your ETA if your arrival time changes using the button below
                    </p>
                </div>
            )}
            <div className="bg-white rounded-2xl shadow-xl p-5 border border-gray-200/50">
                <button
                    type="button"
                    onClick={calculateETA}
                    disabled={disabled}
                    className="w-full cursor-pointer bg-linear-to-r from-gray-900 to-gray-800 hover:from-gray-800 hover:to-gray-700 text-white font-bold py-4 rounded-xl flex items-center justify-center gap-3 transition-all duration-200 shadow-lg hover:shadow-xl hover:scale-[1.02] active:scale-[0.98]"
                >
                    <FontAwesomeIcon icon={faLocationDot} className="w-5 h-5" />
                    USE MY LOCATION
                </button>
                <p className="text-xs text-center text-gray-500 mt-3 font-medium">
                    Share your calculated ETA with the team
                </p>
            </div>
            <ConfirmETA
                deliveryCopy={deliveryCopy}
                visible={showConfirmETAModal}
                hideModal={() => setShowConfirmETAModal(false)}
                updateDeliveryCopy={(delivery) => setDeliveryCopy(delivery)}
                saveDelivery={props.saveDelivery}
            />
        </section>
    );
};

export default ShareETA;
